import { observer } from "mobx-react";
import { useAppBar } from "../appBar/useAppBar";
import { FullScreenLayout } from "../components-v2/FullScreenLayout";
import { useEffect } from "react";
import { useDispatch, useRootStore, useTranslation } from "../stores/RootStoreContext";
import {
  initDocumentForViewingPage,
  openDocumentAttachment,
  prepDocumentForViewingAppBar,
  prepDownloadAsPdfButton,
} from "./documentDomain";
import { DocumentForViewingReadyState } from "./documentApi";
import { AppBarData } from "../appBar/appBarDomain";
import { Card, Grid, GridItem, VBox } from "../components-v2/Box";
import { Alert } from "../components-v2/Alert";
import { Markdown } from "../text/Markdown";
import { AttachmentsCard, DownloadAsPdfButton } from "../common/commonComponents";
import { LandingPageHeader } from "../common/landingPageComponents";
import { PdfPreview } from "../components-v2/PdfPreview";
import { useSize } from "../components-v2/useSize";

export const DocumentForViewingPage = observer(
  ({ documentId, recipientId }: { documentId: string; recipientId?: string }) => {
    const appBar = useAppBar();
    const dispatch = useDispatch();
    const { documentForViewing } = useRootStore();

    useEffect(() => {
      dispatch(initDocumentForViewingPage({ documentId, recipientId }));
    }, [dispatch, documentId, recipientId]);

    switch (documentForViewing?.type) {
      case undefined:
      case "pending":
        return <FullScreenLayout appBar={appBar} centerPanel={"loader"} />;
      case "ready":
        return <DocumentForViewing appBar={appBar} state={documentForViewing} />;
      case "failure":
        return (
          <FullScreenLayout
            appBar={appBar}
            centerPanel={
              <VBox container padding>
                <Alert type="error" icon message={{ key: "general-error" }} />
              </VBox>
            }
          />
        );
    }
  },
);

export const DocumentForViewing = observer(
  ({ appBar, state }: { appBar: AppBarData | null; state: DocumentForViewingReadyState }) => {
    const { t, lang } = useTranslation();
    const dispatch = useDispatch();
    const { size, setRef } = useSize();
    return (
      <FullScreenLayout
        appBar={
          <LandingPageHeader
            {...prepDocumentForViewingAppBar({
              appBar,
              dispatch,
              lang,
              response: state.data,
            })}
          />
        }
        centerPanel={
          <VBox container maxWidth="xl">
            <Grid gap={"lg"} marginY={"lg"}>
              <GridItem xs={12} lg={8}>
                <div ref={setRef}>
                  <PdfPreview file={{ url: state.data.downloadPdfUrl }} width={size.width} />
                </div>
              </GridItem>
              <GridItem xs={12} lg={4}>
                <VBox gap={"md"}>
                  <Card>
                    <DownloadAsPdfButton
                      {...prepDownloadAsPdfButton(state, { variant: "secondary" })}
                    />
                  </Card>
                  <AttachmentsCard
                    attachments={state.data.attachments}
                    onDownload={(id) => dispatch(openDocumentAttachment(state.data, id))}
                  />
                  <Card title={{ key: "document-for-viewing.document-details" }}>
                    <Markdown
                      value={t("document-for-viewing.document-details-text", {
                        documentTitle: state.data.title,
                        senderName: state.data.senderName,
                        recipientName: state.data.recipientName,
                        companyName: state.data.companyName,
                        orgNo: state.data.orgNo,
                      })}
                    />
                  </Card>
                </VBox>
              </GridItem>
            </Grid>
          </VBox>
        }
      />
    );
  },
);
